import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  ArrowRight, Sparkles, UserCheck, Clock3, MapPinned, Moon, CalendarClock,
  Star, Briefcase,
} from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1616805111699-0e52fa62f779?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwY2hhdWZmZXVyJTIwZHJpdmluZ3xlbnwwfHx8fDE3ODQ4MzQ5MDZ8MA&ixlib=rb-4.1.0&q=85";

export default function ServiceVTC() {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="service-vtc-page">
      <Header variant="vtc" />

      {/* HERO */}
      <section
        className="relative pt-32 pb-32 overflow-hidden grain"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(5,5,5,.92) 0%, rgba(5,5,5,.65) 55%, rgba(5,5,5,.3) 100%), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 reveal">
          <div className="text-xs tracking-[0.55em] gold uppercase mb-6">
            Service #02 — VTC
          </div>
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">
            Un chauffeur privé.<br />
            <span className="gold">Votre temps</span>, respecté.
          </h1>
          <p className="mt-8 text-lg text-gray-300 max-w-2xl leading-relaxed">
            Trajets ponctuels ou mise à disposition avec chauffeur — pour vos
            déplacements professionnels, événements, aéroports, dîners ou
            journées entières.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/vtc"
              data-testid="vtc-cta-primary"
              className="btn-gold inline-flex items-center gap-2"
            >
              Réserver maintenant <ArrowRight size={18} />
            </Link>
            <a href="#formules" className="btn-outline-gold inline-flex items-center gap-2">
              Voir les formules
            </a>
          </div>
        </div>
      </section>

      {/* Two formules */}
      <section id="formules" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.5em] gold uppercase text-center">
            Deux formules
          </div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl text-center">
            Choisissez selon votre besoin.
          </h2>

          <div className="mt-16 grid lg:grid-cols-2 gap-8">
            <FormulaCard
              testId="formula-trip"
              icon={MapPinned}
              badge="Trajet ponctuel"
              title="Un point A vers un point B"
              price="20 € + 1,30 → 1,00 € / km"
              features={[
                "Prise en charge fixe de 20 €",
                "Distance à tarif dégressif (comme le convoyage)",
                "Majoration +20% dimanche & jours fériés",
                "Majoration +20% nuit 22h – 06h",
                "Paiement 100% à la réservation",
              ]}
              cta="Réserver un trajet"
            />
            <FormulaCard
              testId="formula-dispo"
              icon={Briefcase}
              badge="Mise à disposition"
              title="Chauffeur à l'heure"
              price="45 € / h · min. 2h"
              features={[
                "Facturé à l'heure — minimum 2h (90 €)",
                "Tarif nuit 55 €/h (22h – 06h)",
                "Majoration +20% dimanche & jours fériés",
                "Acompte 30% à la réservation, solde en fin de prestation",
                "Idéal événements, mariages, journées d'affaires",
              ]}
              cta="Réserver un chauffeur"
              gold
            />
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-24 border-t border-white/5 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.5em] gold uppercase text-center">
            Pour quelles occasions ?
          </div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl text-center mb-16">
            Chaque instant mérite le bon service.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {USE_CASES.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="p-8 border border-white/10 bg-black hover:border-[color:var(--gold)]/40 transition-colors duration-500"
              >
                <Icon size={26} className="gold mb-4" />
                <div className="font-serif text-xl text-white mb-2">{title}</div>
                <p className="text-sm text-gray-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid sm:grid-cols-3 gap-6 text-center">
          {[
            { icon: UserCheck, label: "Chauffeurs certifiés", value: "VTC" },
            { icon: Clock3, label: "Disponible", value: "7j / 7" },
            { icon: Sparkles, label: "Véhicules", value: "Premium" },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="p-8 border border-white/10 bg-[#0a0a0a]">
              <Icon size={22} className="gold mx-auto mb-3" />
              <div className="font-serif text-3xl text-white">{value}</div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mt-1">
                {label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 border-t border-white/5 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs tracking-[0.5em] gold uppercase">Prêt ?</div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl">
            Un chauffeur à vos côtés, quand vous le souhaitez.
          </h2>
          <Link
            to="/vtc"
            data-testid="vtc-cta-final"
            className="btn-gold inline-flex items-center gap-2 mt-8"
          >
            Réserver un VTC <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const USE_CASES = [
  { icon: MapPinned, title: "Aéroports & gares", text: "Prise en charge fiable, prise en compte des retards de vol, suivi en temps réel." },
  { icon: Briefcase, title: "Déplacements pros", text: "Rendez-vous à l'heure, temps de trajet maîtrisé, factures fournies." },
  { icon: Star, title: "Mariages & événements", text: "Prestige, ponctualité, discrétion pour vos moments importants." },
  { icon: Moon, title: "Soirées & retour tardif", text: "Rentrez en sécurité, à n'importe quelle heure de la nuit." },
  { icon: CalendarClock, title: "Journées entières", text: "Mise à disposition à l'heure, chauffeur dédié, planning sur-mesure." },
  { icon: Sparkles, title: "Trajets d'exception", text: "Anniversaires, dîners romantiques, expériences uniques." },
];

function FormulaCard({ testId, icon: Icon, badge, title, price, features, cta, gold }) {
  return (
    <div
      data-testid={testId}
      className={`p-10 border relative overflow-hidden ${
        gold
          ? "border-[color:var(--gold)]/40 bg-gradient-to-br from-[#131007] to-[#0a0a0a]"
          : "border-white/10 bg-[#0a0a0a]"
      }`}
    >
      <Icon size={30} className="gold mb-5" />
      <div className="text-[10px] tracking-[0.4em] gold uppercase mb-3">{badge}</div>
      <h3 className="font-serif text-3xl text-white mb-6">{title}</h3>
      <div className="font-serif text-2xl text-white mb-8">{price}</div>
      <ul className="space-y-3 mb-8">
        {features.map((f) => (
          <li key={f} className="text-sm text-gray-300 flex items-start gap-3">
            <span className="gold mt-1">◆</span>
            {f}
          </li>
        ))}
      </ul>
      <Link to="/vtc" className="btn-gold inline-flex items-center gap-2">
        {cta} <ArrowRight size={16} />
      </Link>
    </div>
  );
}
